import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../auth/authStore'
import {
  getNomenclatureRefStatus,
  importNomenclatureRef,
  type NomenclatureRefImportResult,
} from '../../shared/api/nomenclatureRef.api'
import { PageLoader } from '../../shared/ui/Spinner'
import { EmptyState } from '../../shared/ui/EmptyState'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { toast } from '../../shared/ui/toastStore'

const VIEW_ROLES = ['ADMIN', 'GENERAL_DIRECTOR', 'ACCOUNTANT']
const UPLOAD_ROLES = ['ADMIN', 'ACCOUNTANT']
/** Справочник старше — подсвечиваем: шаблоны счетов могут заводить в УНФ дубли. */
const STALE_DAYS = 8

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })
const fmtCount = (n: number) => n.toLocaleString('ru-RU')

/** Текст ошибки от API, иначе общее сообщение. */
function apiErrorText(e: unknown): string {
  const msg = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data
    ?.error?.message
  return msg ?? 'Не удалось загрузить справочник'
}

/**
 * «Номенклатура УНФ» (docs/features/unf-nomenclature-dupes.md): бухгалтерия раз в неделю загружает
 * выгрузку номенклатуры из боевой УНФ. По ней шаблон счёта приводит партномера к карточкам УНФ —
 * «Загрузка заказов» в УНФ не заводит дубли.
 */
export function NomenclatureRefPage() {
  const role = useAuthStore((s) => s.user?.role)
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [result, setResult] = useState<NomenclatureRefImportResult | null>(null)
  const canView = !!role && VIEW_ROLES.includes(role)
  const canUpload = !!role && UPLOAD_ROLES.includes(role)

  const status = useQuery({
    queryKey: ['nomenclature-ref-status'],
    queryFn: getNomenclatureRefStatus,
    enabled: canView,
  })
  const upload = useMutation({
    mutationFn: (f: File) => importNomenclatureRef(f),
    onSuccess: (r) => {
      setResult(r)
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
      toast.success(`Справочник обновлён: ${fmtCount(r.loaded)} товаров`)
      void qc.invalidateQueries({ queryKey: ['nomenclature-ref-status'] })
    },
    onError: (e) => toast.error(apiErrorText(e)),
  })

  if (!canView) {
    return (
      <div className="p-6">
        <EmptyState title="Раздел недоступен" description="Для вашей роли этот раздел не открыт." />
      </div>
    )
  }

  const loadedAt = status.data?.loadedAt ?? null
  const ageDays = loadedAt
    ? Math.floor((Date.now() - new Date(loadedAt).getTime()) / 86_400_000)
    : null
  const stale = ageDays === null || ageDays >= STALE_DAYS

  return (
    <div className="p-4 sm:p-6 max-w-3xl space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Номенклатура УНФ</h1>
        <p className="mt-1 text-sm text-gray-500">
          По этому справочнику шаблон счёта подставляет артикул из карточки УНФ, даже если в заявке
          партномер написан иначе (пробел, «_» вместо «-», ноль вместо O). Тогда «Загрузка заказов»
          в УНФ находит товар, а не заводит дубль. Обновляйте раз в неделю.
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4">
        {status.isLoading ? (
          <PageLoader />
        ) : status.isError ? (
          <ErrorMessage error={status.error} />
        ) : (
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 text-sm">
            <div>
              <span className="text-gray-500">Товаров в справочнике: </span>
              <span className="font-medium text-gray-900">{fmtCount(status.data?.count ?? 0)}</span>
            </div>
            <div>
              <span className="text-gray-500">Загружен: </span>
              <span className={`font-medium ${stale ? 'text-amber-700' : 'text-gray-900'}`}>
                {loadedAt ? fmtDate(loadedAt) : '—'}
              </span>
              {stale && loadedAt && (
                <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">
                  пора обновить
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {canUpload && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
          <h2 className="text-sm font-semibold text-gray-800">Загрузить новый справочник</h2>
          <ol className="list-decimal pl-5 text-sm text-gray-600 space-y-1">
            <li>
              В УНФ откройте «Склад → Номенклатура», слева «Все группы», поиск и отборы пустые.
            </li>
            <li>
              «Ещё → Вывести список…» — должны быть столбцы «Наименование» и «Артикул» (и «Код»).
            </li>
            <li>В открывшейся таблице «Файл → Сохранить как» → Excel (.xlsx).</li>
            <li>
              Выберите этот файл ниже и нажмите «Загрузить». Старый справочник заменится целиком.
            </li>
          </ol>
          <div className="flex flex-wrap items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="text-sm text-gray-700 file:mr-3 file:rounded-lg file:border file:border-gray-300 file:bg-white file:px-3 file:py-1.5 file:text-sm hover:file:bg-gray-50"
            />
            <button
              type="button"
              disabled={!file || upload.isPending}
              onClick={() => file && upload.mutate(file)}
              className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-50"
            >
              {upload.isPending ? 'Загружаю…' : 'Загрузить'}
            </button>
          </div>
          {result && (
            <p className="text-sm text-gray-700">
              Загружено {fmtCount(result.loaded)} товаров (было {fmtCount(result.previous)}). Строк
              в файле: {fmtCount(result.total)}
              {result.skippedNoArticle > 0 &&
                `, без артикула пропущено: ${fmtCount(result.skippedNoArticle)} — «Загрузка заказов» ищет товар только по артикулу`}
              .
            </p>
          )}
        </div>
      )}
    </div>
  )
}
